import React, { useState } from 'react';
import { Box, Typography, Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface AnalysisData {
  name: string;
  count: number;
  percentage: number;
}

interface TopAnalysesProps {
  data: AnalysisData[];
}

const TopAnalyses: React.FC<TopAnalysesProps> = ({ data }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<SVGCircleElement | null>(null);
  const [popoverContent, setPopoverContent] = useState<string>('');

  const handlePopoverOpen = (event: React.MouseEvent<SVGCircleElement>, count: number) => {
    setAnchorEl(event.currentTarget);
    setPopoverContent(`${count} utilisations`);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Nombre de points sur la toile (nombre d'approches)
  const numPoints = data.length;
  // Rayon du graphique
  const radius = 100;
  // Centre du SVG
  const center = 140;
  // Couleur de la courbe
  const curveColor = '#FF4B8C';

  // Calculer les coordonnées des points sur le cercle
  const getPointCoordinates = (index: number, radius: number) => {
    const angle = (index * 2 * Math.PI) / numPoints - Math.PI / 2;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
      labelX: center + (radius + 25) * Math.cos(angle),
      labelY: center + (radius + 25) * Math.sin(angle)
    };
  };

  // Générer les points de la grille de fond
  const generateGridPoints = (level: number) => {
    const points = [];
    for (let i = 0; i < numPoints; i++) {
      const coord = getPointCoordinates(i, radius * (level / 4));
      points.push(coord);
    }
    return points;
  };

  // Trouver la valeur maximale pour l'échelle
  const maxCount = Math.max(...data.map(d => d.count));
  
  // Générer le chemin pour la courbe de données
  const generateDataPath = () => {
    return data.map((item, i) => {
      const value = (item.count / maxCount) * radius;
      const coord = getPointCoordinates(i, value);
      return `${i === 0 ? 'M' : 'L'} ${coord.x} ${coord.y}`;
    }).join(' ') + ' Z';
  };

  // Points de la grille
  const gridLevels = [1, 2, 3, 4];

  return (
    <Box sx={{ 
      height: '100%', 
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      p: 2,
      position: 'relative'
    }}>
      <svg width="280" height="280" viewBox="0 0 280 280">
        {/* Grille de fond */}
        {gridLevels.map((level) => {
          const points = generateGridPoints(level);
          return (
            <g key={level}>
              <path
                d={points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z'}
                fill="none"
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="1"
              />
            </g>
          );
        })}

        {/* Lignes de connexion au centre */}
        {data.map((_, i) => {
          const point = getPointCoordinates(i, radius);
          return (
            <line
              key={`line-${i}`}
              x1={center}
              y1={center}
              x2={point.x}
              y2={point.y}
              stroke="rgba(255, 255, 255, 0.05)"
              strokeWidth="10"
            />
          );
        })}

        {/* Courbe de données */}
        <g>
          <path
            d={generateDataPath()}
            fill={`${curveColor}20`}
            stroke={curveColor}
            strokeWidth="2"
          />
          {data.map((item, i) => {
            const value = (item.count / maxCount) * radius;
            const coord = getPointCoordinates(i, value);
            return (
              <circle
                key={`point-${i}`}
                cx={coord.x}
                cy={coord.y}
                r="6"
                fill="#1a1b35"
                stroke={curveColor}
                strokeWidth="2"
                onMouseEnter={(e) => handlePopoverOpen(e, item.count)}
                onMouseLeave={handlePopoverClose}
                style={{ cursor: 'pointer' }}
              />
            );
          })}
        </g>

        {/* Points et labels aux extrémités */}
        {data.map((item, i) => {
          const point = getPointCoordinates(i, radius);
          const angle = (i * 2 * Math.PI) / numPoints - Math.PI / 2;
          // Déplace uniquement les labels des points à 90° et 270° (côtés)
          const isSidePoint = Math.abs(Math.sin(angle)) < 0.1;
          const labelOffset = isSidePoint ? -20 : 0;
          
          return (
            <g key={`endpoint-${i}`}>
              <circle
                cx={point.x}
                cy={point.y}
                r="3"
                fill="#ffb74d"
              />
              <text
                x={point.labelX}
                y={point.labelY + labelOffset}
                fill="#ffb74d"
                fontSize="12"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {item.name}
              </text>
            </g>
          );
        })}
      </svg>

      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box sx={{ 
          bgcolor: 'rgba(26, 27, 53, 0.95)',
          p: 1,
          borderRadius: 1
        }}>
          <Typography sx={{ color: curveColor }}>
            {popoverContent}
          </Typography>
        </Box>
      </Popover>

      {/* Légende */}
      <Box sx={{         
        position: 'absolute',
        bottom: -1,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 2,
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ 
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: curveColor
          }} />
          <Typography sx={{ color: curveColor, fontSize: '0.8rem' }}>
            {t('statistics.topAnalyses.usageCount')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TopAnalyses;
